import { useId, useState } from "react";
import { Button } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { usePlotOutline } from "../../hooks/usePlotOutline";
import { type SeedStripe } from "../../utils/seedFlow";
import { getNonEmptySeedColor } from "../../utils/stripSeedPresentation";
import useSeedTranslations from "../../hooks/useSeedTranslations";
import SeedRateInput from "../SeedRateInput";
import "./style.less";

interface StripeManualAssignmentTableProps {
  stripes: SeedStripe[];
  rates: Record<string, number | null>;
  errors: Record<string, string | undefined>;
  colors?: Record<string, string>;
  unit?: string;
  rateReadOnly?: boolean;
  rateOptions?: number[];
  accentColor?: string;
  highlightedId?: string | null;
  onHighlightChange?: (id: string | null) => void;
  onRateChange: (id: string, rate: number | null) => void;
}

const StripeManualAssignmentTable = ({
  stripes,
  rates,
  errors,
  colors,
  unit = "seeds/ha",
  rateReadOnly = false,
  rateOptions,
  accentColor = "#2fb1e6",
  highlightedId,
  onHighlightChange,
  onRateChange,
}: StripeManualAssignmentTableProps): JSX.Element | null => {
  const text = useSeedTranslations();
  const instanceId = useId();
  const { isOutlined, toggleOutline } = usePlotOutline();

  const [localHighlightedId, setLocalHighlightedId] = useState<string | null>(
    null
  );

  const activeHighlightedId =
    highlightedId === undefined ? localHighlightedId : highlightedId;

  const selectedStripes = stripes.filter((stripe) =>
    Object.prototype.hasOwnProperty.call(rates, stripe.id)
  );

  if (selectedStripes.length === 0) {
    return null;
  }

  return (
    <div className="rd-stripe-manual-table__wrapper">
      <table className="rd-stripe-manual-table">
        <thead>
          <tr>
            <th scope="col">{text("strips")}</th>
            <th scope="col">
              {text("seedRate")} <span>({unit})</span>
            </th>
            <th scope="col">
              <span className="rd-stripe-manual-table__sr-only">
                {text("highlightStripe")}
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          {selectedStripes.map((stripe) => {
            const stripeNumber =
              stripes.findIndex((item) => item.id === stripe.id) + 1;

            const error = errors[stripe.id];
            const errorId = `${instanceId}-${stripe.id}-rate-error`;
            const highlighted = activeHighlightedId === stripe.id;

            const color =
              getNonEmptySeedColor(colors?.[stripe.id], accentColor) ??
              "#2fb1e6";

            return (
              <tr
                key={stripe.id}
                className={
                  highlighted
                    ? "rd-stripe-manual-table__row--highlighted"
                    : undefined
                }
              >
                <th scope="row" style={{ borderLeftColor: color }}>
                  {stripeNumber}
                </th>

                <td>
                  <SeedRateInput
                    options={rateOptions}
                    aria-label={text("stripeRateAria", {
                      stripe: stripe.label,
                    })}
                    value={rates[stripe.id]}
                    readOnly={rateReadOnly}
                    disabled={rateReadOnly}
                    status={error ? "error" : undefined}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                    onChange={(rate) => {
                      if (!rateReadOnly) {
                        onRateChange(stripe.id, rate);
                      }
                    }}
                  />

                  {error && (
                    <span
                      id={errorId}
                      className="rd-stripe-manual-table__error"
                      role="alert"
                    >
                      {error}
                    </span>
                  )}
                </td>

                <td>
                  <Button
                    type="text"
                    icon={
                      isOutlined(stripe.id) ? (
                        <EyeOutlined />
                      ) : (
                        <EyeInvisibleOutlined />
                      )
                    }
                    aria-label={text("highlightStripeAria", {
                      stripe: stripe.label,
                    })}
                    aria-pressed={isOutlined(stripe.id)}
                    onClick={() => {
                      toggleOutline(stripe.id);
                      const nextId = highlighted ? null : stripe.id;

                      if (highlightedId === undefined) {
                        setLocalHighlightedId(nextId);
                      }

                      onHighlightChange?.(nextId);
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default StripeManualAssignmentTable;
